import { VisitorEvent } from './../../models/visitor.event';
import { ConfigService } from './../../../../common/services/config/config.service';
import { GoogleMapService } from './../../services/google.map.service';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
  inject,
} from '@angular/core';
import {
  delay,
  map,
  Observable,
  Subject,
  takeUntil,
  firstValueFrom,
  combineLatest,
  of,
} from 'rxjs';
import {
  GoogleMap,
  MapDirectionsService,
  MapInfoWindow,
  MapMarker,
  MapMarkerClusterer,
} from '@angular/google-maps';
import { VisitorPlace } from '../../models/visitor.place';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { FancyPlaceDetailsModalComponent } from 'src/app/modules/place-calendar/components/place.details.modal/fancy.place.details.modal/fancy.place.details.modal.component';
import { IPlaceDetailsModalData } from 'src/app/modules/place-calendar/components/place.details.modal/place.details.modal.provider';
import { FancyEventDetailsModalComponent } from 'src/app/modules/event-calendar/components/event.details.modal/fancy.event.details.modal/fancy.event.details.modal.component';
import { IEventDetailsModalData } from 'src/app/modules/event-calendar/components/event.details.modal/event.details.modal.provider';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { IMapMarkerWithCustomData } from '../../models/place.marker';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { ActivatedRoute } from '@angular/router';
import { SsrDetailNavigationService } from '../../services/ssr.detail.navigation.service';
import { IFrontendSettings } from '../../ngrx.stores/frontend.settings/states';

export interface IClusterPosition {
  cluster: any;
  rect: DOMRect;
  infowindow: google.maps.InfoWindow | null;
  isInfoWindowOpen: boolean;
  marker: google.maps.Marker | null;
  el: HTMLElement;
  overlay?: google.maps.OverlayView | null;
}

@Component({
  selector: 'ig-google-map',
  templateUrl: './google.map.component.html',
  styleUrls: ['./google.map.component.scss'],
  host: {
    class: 'ig-google-map',
  },
  encapsulation: ViewEncapsulation.None,
})
export class GoogleMapComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') hostClass = 'w-full block';

  // Component state - Enhanced with detailed tracking
  private pendingMarkers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] = [];
  private mapInitialized = false;
  private apiReady = false;
  private initializationAttempted = false;
  private initializationComplete = false;
  private readonly destroy$ = new Subject<void>();
  private debugMode = true; // Set to false in production

  // Observables
  apiLoaded$: Observable<boolean>;
  directionsResults$: Observable<google.maps.DirectionsResult | undefined>;

  // Inputs
  @Input() useCluster = true; // Disabled by default to prevent ByteString errors
  @Input() extraFitBounds = true;
  @Input() zoom = 14;
  @Input() useDefaultMarkerClickStyle = true;
  @Input() center: google.maps.LatLngLiteral = ConfigService.config.google.USCenter;
  @Input() canInfoWindowClick = true;
  @Input() zoomControlPosition?: string;
  @Input() frontendSettings?: IFrontendSettings | null;

  // New input to specify hub names that need fallback loading (mapId not working for them)
  hubName = '';
  fallbackForClients: string[] = ['LakeCoOH', 'AshlandKY', 'HalifaxNC', 'AppalachianWV'];

  // Enhanced markers handling
  _markers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] = [];

  @Input() set markers(value: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[]) {
    this.debug('Setting markers:', value?.length || 0);

    const validatedMarkers = this.validateMarkers(value || []);
    this._markers = validatedMarkers;
    this.pendingMarkers = [...validatedMarkers];
    // The marker set was replaced, so any previous highlight reference is stale.
    this._highlightedMarker = null;

    // Only process if everything is ready
    if (this.isFullyReady()) {
      this.debug('Component fully ready, processing markers immediately');
      this.processMarkersWithDelay();
    } else {
      this.debug('Component not ready, markers queued. Status:', this.getComponentStatus());
    }
  }

  // Mode with proper options update
  _mode: 'light' | 'dark' = 'light';
  @Input() set mode(value: 'light' | 'dark') {
    this._mode = value;
    if (this.mapInitialized) {
      this.updateMapOptions();
    }
  }

  // Outputs
  @Output() public onCloseInfoWindow = new EventEmitter();
  @Output() public onInfoWindowClick = new EventEmitter<VisitorEvent | VisitorPlace>();
  @Output() public onDetailsWindowClose = new EventEmitter<VisitorEvent | VisitorPlace>();
  @Output() public onclick = new EventEmitter();
  @Output() public onMarkerClick = new EventEmitter<
    IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  >();

  // UI state
  isShowEventInfoWindow = false;
  isShowPlaceInfoWindow = false;
  isShowClusterInfoWindow = false;
  isMobile = false;

  // Map configuration - Safer defaults
  options: google.maps.MapOptions;
  directionsMarkerOptions: google.maps.DirectionsRendererOptions = {
    markerOptions: { opacity: 0 },
    suppressMarkers: true,
  };

  // Component references
  @ViewChild(GoogleMap, { static: false }) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow!: MapInfoWindow;
  @ViewChildren(MapMarker) mapMarkerObjs!: QueryList<MapMarker>;
  @ViewChild(MapMarkerClusterer) markerCluster!: MapMarkerClusterer;

  // Selected marker state
  selectedMarker: {
    markerEle: MapMarker;
    marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>;
  } | null = null;

  // The marker currently styled as "highlighted". Tracked so we only restyle the
  // previous + new selection instead of every marker (which would break clustering).
  private _highlightedMarker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace> | null = null;

  // Cluster state
  clusterPositions: IClusterPosition[] = [];
  private clusterAbortController: AbortController | null = null;
  private clusterCloseTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private clusterPopupClickHandler: ((e: Event) => void) | null = null;
  private clusterPopupItems: { el: HTMLElement; marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace> }[] = [];

  constructor(
    public googleMapService: GoogleMapService,
    public modalService: ModalService,
    public mapDirectionsService: MapDirectionsService,
    public scriptService: ScriptService,
    public mediaQueryService: MediaQueryService,
    private _placeService: VisitorPlaceService,
    private _renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private ssrDetailNavigationService: SsrDetailNavigationService
  ) {
    this.debug('Constructor: Initializing component');

    // Remove the competing script load from constructor
    // The GoogleMapService will handle script loading properly

    // Force disable clustering to prevent ByteString errors
    //this.useCluster = false;

    this.extractHubName();

    this.initializeComponent();
  }

  private debug(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(`🗺️ [GoogleMap] ${message}`, ...args);
    }
  }

  private extractHubName(): void {
    // Try to get hubName from route parameters
    // try {
    //   this.activatedRoute.params.subscribe((params) => {
    //     this.hubName = params['hubName'] || this.hubName;
    //     console.log('[GoogleMap] Detected hubName in activatedRoute:', this.hubName);
    //   });
    // } catch (error) {
    //   console.warn('[GoogleMap] Could not extract hubName from route:', error);
    // }

    // Fallback to getting hubName from BaseComponent approach
    if (!this.hubName) {
      const wrapper =
        document.getElementById('imgoingcalendar-wrapper') ||
        document.getElementById('imgoingcalendar-wrapper-wix');
      if (wrapper) {
        const routeAttr = wrapper.getAttribute('route') || wrapper.getAttribute('data-route');
        if (routeAttr) {
          const url = routeAttr;
          const noSlash = url.startsWith('/') ? url.substr(1) : url;
          this.hubName = noSlash.substr(0, noSlash.indexOf('/'));
        }
      }
    }

    console.log('[GoogleMap] Detected hubName:', this.hubName);
  }

  private async initializeComponent(): Promise<void> {
    try {
      this.debug('Starting component initialization');

      // Setup mobile detection
      this.setupMobileDetection();

      // Initialize safe map options first
      //this.initializeMapOptions();
      if (this.useCluster) {
        await Promise.all([this.loadMarkerClusterScript()]);
      }

      this.debug('Component initialization completed');
    } catch (error) {
      console.error('❌ [GoogleMap] Component initialization failed:', error);
      this.handleInitializationError(error);
    }
  }

  private setupMobileDetection(): void {
    this.mediaQueryService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.debug('Mobile state changed:', isMobile);
    });
  }

  async ngOnInit(): Promise<void> {
    this.debug('ngOnInit started');
    this.initializeMapOptions();

    try {
      // Ensure apiLoaded$ is initialized before awaiting
      if (!this.apiLoaded$) {
        this.apiLoaded$ = this.googleMapService.loadGoogleMapApi();
      }
      // Wait for API to load
      const apiLoaded = await firstValueFrom(this.apiLoaded$);
      if (apiLoaded) {
        this.apiReady = true;
        console.log('[GoogleMap] API loaded successfully');
      } else {
        throw new Error('Failed to load Google Maps API');
      }
    } catch (error) {
      console.error('[GoogleMap] Map service initialization failed:', error);
      throw error;
    }
    // }

    this.debug('ngOnInit completed');
  }

  private async loadMarkerClusterScript(): Promise<void> {
    const SCRIPT_PATH = 'https://unpkg.com/@googlemaps/markerclustererplus/dist/index.min.js';

    if (this.scriptService.isScriptExist(SCRIPT_PATH)) {
      console.log('[GoogleMap] Marker cluster script already loaded');
      return;
    }

    return new Promise((resolve, reject) => {
      const scriptElement = this.scriptService.loadJsScript(this._renderer, SCRIPT_PATH);

      scriptElement.onload = () => {
        console.log('[GoogleMap] Marker cluster script loaded successfully');
        resolve();
      };

      scriptElement.onerror = (error) => {
        console.error('[GoogleMap] Failed to load marker cluster script:', error);
        reject(new Error('Failed to load marker cluster script'));
      };
    });
  }

  private initializeMapOptions(): void {
    this.debug('Initializing ultra-safe map options');

    // Ultra-safe options that won't cause ByteString errors
    this.options = {
      mapId:
        this._mode === 'light'
          ? ConfigService.config.google.mapId
          : ConfigService.config.google.darkModeMapId,
      center: this.center,
      zoom: this.zoom,
      // Minimal UI controls to prevent issues
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      // Safe interaction settings
      gestureHandling: 'auto',
      clickableIcons: false,
    };

    // these client site doesn't support mapId
    if (this.fallbackForClients.includes(this.hubName)) {
      delete this.options.mapId;
      console.log(`======= [GoogleMap] mapId removed for client ${this.hubName}`);
    }

    this.debug('Ultra-safe map options initialized');
  }

  private updateMapOptions(): void {
    if (this.options && this.mapInitialized) {
      this.debug('Updating map options');

      // Only update safe properties
      const safeUpdate = {
        ...this.options,
        mapId:
          this._mode === 'light'
            ? ConfigService.config.google.mapId
            : ConfigService.config.google.darkModeMapId,
      };

      this.options = safeUpdate;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.debug('ngAfterViewInit - Starting initialization sequence');

    // Use NgZone to handle async operations properly
    this.ngZone.runOutsideAngular(() => {
      this.startInitializationSequence();
    });
  }

  private async startInitializationSequence(): Promise<void> {
    if (this.initializationAttempted) {
      this.debug('Initialization already attempted, skipping');
      return;
    }

    this.initializationAttempted = true;
    this.debug('Starting initialization sequence');

    try {
      // Step 1: Wait for Google Maps API to load
      await this.waitForGoogleMapsAPI();

      // Step 2: Wait for Angular Google Maps component to initialize
      await this.waitForMapComponentReady();

      // Step 3: Mark as fully initialized
      this.markAsInitialized();

      this.ngZone.run(() => {
        this.applyCustomControlOptions();
      });

      // Step 4: Process any pending markers
      this.ngZone.run(() => {
        this.processPendingMarkers();
      });
    } catch (error) {
      console.error('❌ [GoogleMap] Initialization sequence failed:', error);
      this.handleInitializationError(error);
    }
  }

  private async waitForGoogleMapsAPI(): Promise<void> {
    this.debug('Step 1: Waiting for Google Maps API to load...');

    try {
      // Start loading the API
      this.apiLoaded$ = this.googleMapService.loadGoogleMapApi();

      // Wait for it to complete with timeout
      const apiLoaded = await firstValueFrom(this.apiLoaded$);

      if (!apiLoaded) {
        throw new Error('Google Maps API failed to load');
      }

      // Double-check API is actually ready
      const apiReady = await this.googleMapService.waitForApiReady(10000);
      if (!apiReady) {
        throw new Error('Google Maps API not ready after loading');
      }

      this.apiReady = true;
      this.debug('✅ Step 1 completed: Google Maps API loaded and ready');
    } catch (error) {
      this.debug('❌ Step 1 failed: Google Maps API loading error:', error);
      throw error;
    }
  }

  private async waitForMapComponentReady(): Promise<void> {
    this.debug('Step 2: Waiting for Angular Google Maps component...');

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Map component initialization timeout'));
      }, 15000);

      const checkMapComponent = () => {
        // Check if the Angular Google Maps component is ready
        if (this.googleMap?.googleMap) {
          clearTimeout(timeout);
          this.mapInitialized = true;
          this.debug('✅ Step 2 completed: Angular Google Maps component ready');
          resolve();
        } else {
          // Keep checking every 100ms
          setTimeout(checkMapComponent, 100);
        }
      };

      checkMapComponent();
    });
  }

  private markAsInitialized(): void {
    this.debug('Step 3: Marking component as fully initialized');
    this.initializationComplete = true;

    // Force change detection to update template
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });

    this.debug('✅ Step 3 completed: Component fully initialized');
  }

  private applyCustomControlOptions(): void {
    if (!this.zoomControlPosition) {
      return;
    }
    const map = this.googleMap?.googleMap;
    const controlPositions = (window as any).google?.maps?.ControlPosition;
    if (!map || !controlPositions) {
      return;
    }
    const position = controlPositions[this.zoomControlPosition];
    if (position === undefined) {
      console.warn(
        `[GoogleMap] Unknown zoomControlPosition: "${this.zoomControlPosition}"`,
      );
      return;
    }
    map.setOptions({ zoomControlOptions: { position } });
  }

  private processPendingMarkers(): void {
    this.debug('Step 4: Processing pending markers');

    if (this.pendingMarkers.length > 0) {
      this.debug(`Processing ${this.pendingMarkers.length} pending markers`);
      this.processMarkersWithDelay();
    } else {
      this.debug('No pending markers to process');
    }

    this.debug('✅ Step 4 completed: Pending markers processed');
  }

  private validateMarkers(
    markers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[]
  ): IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] {
    if (!markers || !Array.isArray(markers)) {
      this.debug('Invalid markers array provided');
      return [];
    }

    const validMarkers = markers.filter((marker) => {
      if (!marker?.position) {
        console.warn('⚠️ [GoogleMap] Marker missing position:', marker);
        return false;
      }

      // Normalize position to LatLngLiteral
      let lat: number, lng: number;

      if (typeof marker.position.lat === 'function' && typeof marker.position.lng === 'function') {
        // It's a Google Maps LatLng object
        lat = marker.position.lat();
        lng = marker.position.lng();

        // Convert to LatLngLiteral for consistency
        marker.position = { lat, lng };
      } else if (
        typeof marker.position.lat === 'number' &&
        typeof marker.position.lng === 'number'
      ) {
        // It's already a LatLngLiteral
        lat = marker.position.lat;
        lng = marker.position.lng;
      } else {
        console.warn('⚠️ [GoogleMap] Invalid marker position type:', marker);
        return false;
      }

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn('⚠️ [GoogleMap] Invalid marker coordinates:', { lat, lng }, marker);
        return false;
      }

      return true;
    });

    this.debug(`Validated ${validMarkers.length}/${markers.length} markers`);
    return validMarkers;
  }

  private normalizePosition(position: any): google.maps.LatLngLiteral | null {
    if (!position) return null;

    let lat: number, lng: number;

    if (typeof position.lat === 'function' && typeof position.lng === 'function') {
      // Google Maps LatLng object
      lat = position.lat();
      lng = position.lng();
    } else if (typeof position.lat === 'number' && typeof position.lng === 'number') {
      // Already a LatLngLiteral
      lat = position.lat;
      lng = position.lng;
    } else {
      return null;
    }

    // Validate bounds
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    return { lat, lng };
  }

  private isFullyReady(): boolean {
    const ready =
      this.mapInitialized &&
      this.apiReady &&
      this.initializationComplete &&
      !!this.googleMap?.googleMap;
    this.debug('Full readiness check:', ready);
    return ready;
  }

  private getComponentStatus(): object {
    return {
      mapInitialized: this.mapInitialized,
      apiReady: this.apiReady,
      initializationComplete: this.initializationComplete,
      hasGoogleMap: !!this.googleMap?.googleMap,
      initializationAttempted: this.initializationAttempted,
      pendingMarkersCount: this.pendingMarkers.length,
      currentMarkersCount: this._markers.length,
    };
  }

  private processMarkersWithDelay(): void {
    if (!this.isFullyReady()) {
      this.debug('Not ready to process markers, skipping');
      return;
    }

    this.debug('Processing markers with safety delay');

    // Clean markers first to prevent ByteString errors
    this.cleanMarkersForSafety();

    // Process immediately for better UX now that readiness checks are strict
    this.ngZone.run(() => {
      this.ngZone.run(() => {
        try {
          if (this._markers.length > 0) {
            this.debug(`Processing ${this._markers.length} markers`);
            this.handleMapBounds();
          } else {
            this.debug('No markers to process, setting no content state');
            this.setNoContentState();
          }

          // Clear pending markers
          this.pendingMarkers = [];
        } catch (error) {
          console.error('❌ [GoogleMap] Error processing markers:', error);
        }
      });
    });
  }

  private cleanMarkersForSafety(): void {
    this.debug('Cleaning markers for safety');

    // this._markers.forEach(marker => {
    //   // Normalize position first
    //   const normalizedPosition = this.normalizePosition(marker.position);
    //   if (normalizedPosition) {
    //     marker.position = normalizedPosition;
    //   }

    //   // Ensure safe options
    //   if (marker.options) {
    //     // Create completely clean options
    //     const cleanOptions: any = {};

    //     // Only copy absolutely safe properties
    //     if (typeof marker.options.zIndex === 'number') {
    //       cleanOptions.zIndex = marker.options.zIndex;
    //     }
    //     if (typeof marker.options.opacity === 'number') {
    //       cleanOptions.opacity = marker.options.opacity;
    //     }
    //     if (typeof marker.options.visible === 'boolean') {
    //       cleanOptions.visible = marker.options.visible;
    //     }

    //     // Replace with clean options
    //     marker.options = cleanOptions;
    //   } else {
    //     marker.options = {};
    //   }

    //   // Ensure safe title
    //   if (!marker.title || typeof marker.title !== 'string' || marker.title.trim() === '') {
    //     marker.title = marker.data?.title || marker.data?.name || 'Location';
    //   }

    //   // Clean or remove problematic label
    //   if (marker.label) {
    //     if (typeof marker.label === 'object') {
    //       marker.label = {
    //         text: String((marker.label as any).text || 'M').substring(0, 3),
    //         color: '#000000',
    //         fontSize: '12px'
    //       };
    //     } else if (typeof marker.label === 'string' && marker.label.trim() === '') {
    //       marker.label = undefined;
    //     }
    //   }
    // });

    this.debug('Markers cleaned for safety');
  }

  private handleMapBounds(): void {
    try {
      if (!this.googleMap?.googleMap) {
        console.warn('⚠️ [GoogleMap] Cannot handle bounds - map not ready');
        return;
      }

      if (this._markers.length === 1) {
        // Single marker - center on it with appropriate zoom
        const marker = this._markers[0];
        this.center = {
          lat: marker.position.lat as number,
          lng: marker.position.lng as number,
        };

        // Set appropriate zoom for single marker
        setTimeout(() => {
          if (this.googleMap?.googleMap) {
            this.googleMap.googleMap.setZoom(14);
          }
        }, 100);

        this.debug('Centered on single marker');
      } else if (this._markers.length > 1) {
        // Multiple markers - create bounds
        this.fitMarkersInBounds();
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error handling map bounds:', error);
    }
  }

  private fitMarkersInBounds(): void {
    try {
      const bounds = new google.maps.LatLngBounds();
      let validMarkersCount = 0;

      this._markers.forEach((marker) => {
        const position = this.normalizePosition(marker.position);
        if (position) {
          bounds.extend(new google.maps.LatLng(position.lat, position.lng));
          validMarkersCount++;
        }
      });

      if (validMarkersCount > 1 && this.extraFitBounds) {
        // Add padding to prevent markers being too close to edges
        this.googleMap.fitBounds(bounds, 50);
        this.debug(`Fitted bounds for ${validMarkersCount} markers`);
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error fitting bounds:', error);
    }
  }

  private setNoContentState(): void {
    this.center = ConfigService.config.google.USCenter as google.maps.LatLngLiteral;
    this.options = {
      ...this.options,
      zoom: 5,
    };
    this.debug('Set no content state');
  }

  private handleInitializationError(error: any): void {
    console.error('❌ [GoogleMap] Initialization error:', error);

    const errorMessage = error instanceof Error ? error.message : 'Failed to initialize map';
    this.debug('Initialization failed:', errorMessage);

    // Reset state for potential retry
    this.resetComponentState();

    // You might want to emit an error event or show user notification here
    // this.onInitializationError.emit(errorMessage);
  }

  private resetComponentState(): void {
    this.debug('Resetting component state for retry');

    this.mapInitialized = false;
    this.apiReady = false;
    this.initializationAttempted = false;
    this.initializationComplete = false;
    this.pendingMarkers = [...this._markers];
  }

  // Map interaction methods
  mapClick(event: google.maps.MapMouseEvent): void {
    this.closeInfoWindow();
    this.onclick.emit();
    this.closeClusterInfoWindows();
  }

  markerClick(
    markerEle: MapMarker,
    marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): void {
    try {
      this.debug('Marker click handled safely');

      if (this.useDefaultMarkerClickStyle) {
        this.updateMarkerStyles(marker);
      }

      if (this.navigateMarkerToSsrDetail(marker)) {
        return;
      }

      this.selectedMarker = { markerEle, marker };
      this.onMarkerClick.emit(marker);

      // Determine which info window to show based on data type
      if (marker.data && 'eventLink' in marker.data) {
        this.showEventInfoWindow(markerEle, marker as IMapMarkerWithCustomData<VisitorEvent>);
      } else {
        this.showPlaceInfoWindow(markerEle, marker as IMapMarkerWithCustomData<VisitorPlace>);
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error in marker click:', error);
    }
  }

  private navigateMarkerToSsrDetail(
    marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): boolean {
    if (this.ssrDetailNavigationService.isSsrTripPlannerWidget()) return false;
    if (!this.useDefaultMarkerClickStyle || !marker?.data) {
      return false;
    }

    const isEventMarker = 'eventLink' in marker.data;
    const didNavigate = this.ssrDetailNavigationService.navigateToDetail({
      itemType: isEventMarker ? 'event' : 'place',
      item: marker.data,
      hubName: this.hubName,
      frontendSettings: this.frontendSettings,
    });

    if (didNavigate) {
      this.onInfoWindowClick.emit(marker.data);
    }

    return didNavigate;
  }

  private updateMarkerStyles(
    selectedMarker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): void {
    this.applyMarkerHighlight(selectedMarker);
  }

  /**
   * Highlight a marker by its data id. Lets parent components (e.g. clicking a
   * place card) drive the same selection styling the map uses for pin clicks.
   */
  public highlightMarkerById(id: string): void {
    if (!id) {
      return;
    }
    const marker = this._markers.find((m) => m.data?._id === id || m.title === id);
    if (marker) {
      this.applyMarkerHighlight(marker);
    }
  }

  private applyMarkerHighlight(
    selectedMarker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): void {
    try {
      this.debug('Updating marker styles safely');

      // Already the highlighted marker — just recenter, don't re-mutate options.
      if (this._highlightedMarker === selectedMarker) {
        const samePosition = this.normalizePosition(selectedMarker.position);
        if (samePosition) {
          this.center = samePosition;
        }
        return;
      }

      // Reset ONLY the previously highlighted marker. Resetting every marker
      // re-runs setOptions on all of them, and @angular/google-maps' _combineOptions
      // always sets `map`, which re-attaches each marker to the map and breaks
      // clustering (all pins become visible under the clusters).
      if (this._highlightedMarker && this._highlightedMarker !== selectedMarker) {
        this._highlightedMarker.options = {
          zIndex: 1,
          icon: null,
          animation: null,
        };
      }

      // Highlight selected marker (safely)
      selectedMarker.options = {
        icon: 'https://iti-images.s3.amazonaws.com/imgs/marker-xs.png',
        animation: google.maps.Animation.BOUNCE,
        zIndex: 2,
      };
      this._highlightedMarker = selectedMarker;

      // Update center with normalized position
      const normalizedPosition = this.normalizePosition(selectedMarker.position);
      if (normalizedPosition) {
        this.center = normalizedPosition;
      }

      this.debug('Marker styles updated safely');
    } catch (error) {
      console.error('❌ [GoogleMap] Error updating marker styles:', error);
    }
  }

  // Info window methods
  showEventInfoWindow(marker?: MapMarker, data?: IMapMarkerWithCustomData<VisitorEvent>): void {
    this.isShowPlaceInfoWindow = false;
    this.isShowEventInfoWindow = true;

    if (marker && data) {
      this.selectedMarker = { markerEle: marker, marker: data };
    }

    if (this.selectedMarker) {
      this.infoWindow.open(this.selectedMarker.markerEle);
    }
  }

  showPlaceInfoWindow(marker?: MapMarker, data?: IMapMarkerWithCustomData<VisitorPlace>): void {
    this.isShowEventInfoWindow = false;
    this.isShowPlaceInfoWindow = true;

    if (marker && data) {
      this.selectedMarker = { markerEle: marker, marker: data };
    }

    if (this.selectedMarker) {
      this.infoWindow.open(this.selectedMarker.markerEle);
    }
  }

  closeInfoWindow(): void {
    this.isShowEventInfoWindow = false;
    this.isShowPlaceInfoWindow = false;
    this.infoWindow?.close();
    this.onCloseInfoWindow.emit();
  }

  // Navigation methods

  private exitFullscreenIfNeeded(): Promise<void> {
    const fsElement =
      document.fullscreenElement || (document as any).webkitFullscreenElement;

    if (!fsElement) {
      return Promise.resolve();
    }

    const exitFn: (() => Promise<void>) | undefined =
      document.exitFullscreen?.bind(document) ??
      (document as any).webkitExitFullscreen?.bind(document);

    if (!exitFn) {
      return Promise.resolve();
    }

    return exitFn().catch(() => {});
  }

  goToEvent(event: VisitorEvent): void {
    if (!this.canInfoWindowClick) {
      return;
    }

    this.exitFullscreenIfNeeded().then(() => {
      if (this.ssrDetailNavigationService.navigateToDetail({
        itemType: 'event',
        item: event,
        hubName: this.hubName,
        frontendSettings: this.frontendSettings,
        onPreviewClosed: () => this.onDetailsWindowClose.emit(event),
      })) {
        if (!this.ssrDetailNavigationService.isSsrTripPlannerWidget()) this.onInfoWindowClick.emit(event);
        return;
      }

      try {
        const modal = this.modalService.show({
          component: FancyEventDetailsModalComponent,
          panelClass: 'ig-modal-w-full',
          data: {
            currentEvent: event,
            allEvents: [],
            favCustomEvents: [],
            from: 'placeCalendar',
          } as IEventDetailsModalData,
        });

        this.onInfoWindowClick.emit(event);

        modal.beforeClosed().subscribe(() => {
          this.onDetailsWindowClose.emit(event);
        });
      } catch (error) {
        console.error('[GoogleMap] Error opening event modal:', error);
      }
    });
  }

  goToPlace(place: VisitorPlace): void {
    if (!this.canInfoWindowClick) {
      return;
    }

    this.exitFullscreenIfNeeded().then(() => {
      if (this.ssrDetailNavigationService.navigateToDetail({
        itemType: 'place',
        item: place,
        hubName: this.hubName,
        frontendSettings: this.frontendSettings,
        onPreviewClosed: () => this.onDetailsWindowClose.emit(place),
      })) {
        if (!this.ssrDetailNavigationService.isSsrTripPlannerWidget()) this.onInfoWindowClick.emit(place);
        return;
      }

      try {
        const modal = this.modalService.show({
          component: FancyPlaceDetailsModalComponent,
          panelClass: 'ig-modal-w-full',
          data: {
            currentPlace: place,
          } as IPlaceDetailsModalData,
        });

        modal.beforeClosed().subscribe(() => {
          this.onDetailsWindowClose.emit(place);
        });

        this.onInfoWindowClick.emit(place);
      } catch (error) {
        console.error('[GoogleMap] Error opening place modal:', error);
      }
    });
  }

  // Directions methods
  async drawDirectionRoute(
    locations: { lat: number; lng: number; stopover?: boolean }[] = [],
    travelMode = google.maps.TravelMode.DRIVING,
    option: { optimizeWaypoints: boolean } = { optimizeWaypoints: false }
  ): Promise<void> {
    if (!locations || locations.length <= 1) {
      console.warn('⚠️ [GoogleMap] Invalid locations for directions');
      return;
    }

    // Wait for component to be fully ready
    if (!this.isFullyReady()) {
      console.warn('⚠️ [GoogleMap] Component not ready for directions');
      return;
    }

    try {
      const waypoints = locations.slice(1, -1).map((location) => ({
        location: { lng: location.lng, lat: location.lat },
        stopover: location.stopover ?? true,
      }));

      const request: google.maps.DirectionsRequest = {
        destination: locations[locations.length - 1],
        origin: locations[0],
        waypoints: waypoints,
        travelMode: travelMode,
        ...option,
      };

      console.log('[GoogleMapService] Directions request:', request);
      this.directionsResults$ = this.mapDirectionsService
        .route(request)
        .pipe(map((response) => response.result));
    } catch (error) {
      console.error('❌ [GoogleMap] Error drawing direction route:', error);
    }
  }

  // Utility methods
  getPixelPositionByLatLng(latlng: google.maps.LatLng): Promise<{ x: number; y: number }> {
    return new Promise<{ x: number; y: number }>((resolve, reject) => {
      const overlay = new google.maps.OverlayView();
      overlay.draw = function () {};
      overlay.onAdd = function () {
        const projection = this.getProjection();
        const pixel = projection.fromLatLngToContainerPixel(latlng);
        resolve(pixel);
      };
      overlay.setMap(this.googleMap.googleMap);
    });
  }

  getDisplayCategory(place: VisitorPlace): string {
    return this._placeService.getDisplayCategory(place);
  }

  public fitBounds(padding?: number): void {
    try {
      if (!this.isFullyReady()) {
        console.warn('⚠️ [GoogleMap] Cannot fit bounds - component not ready');
        return;
      }

      this.handleMapBounds();
    } catch (error) {
      console.error('❌ [GoogleMap] Error fitting bounds:', error);
    }
  }

  // Clustering methods (kept but safer)
  clustererEnd($event: any): void {
    try {
      this.closeClusterInfoWindows();
      this.clusterAbortController?.abort();
      this.clusterAbortController = new AbortController();
      this.registerClusterPopupClickHandler();

      setTimeout(() => {
        this.getPositionOfEachCluster($event);
        if (!this.isMobile) {
          this.setupClusterHoverListeners();
        }
      }, 150);
    } catch (error) {
      console.error('❌ [GoogleMap] Error handling cluster end:', error);
    }
  }

  private registerClusterPopupClickHandler(): void {
    // Remove previous handler if any
    if (this.clusterPopupClickHandler) {
      document.removeEventListener('click', this.clusterPopupClickHandler, true);
      this.clusterPopupClickHandler = null;
    }

    this.clusterPopupClickHandler = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const target = e.target as Node;
      if (!target) return;

      // Strategy 1: Walk composedPath() to find our stored elements or data attributes
      const path = mouseEvent.composedPath?.() || [];
      let foundMarker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace> | null = null;
      let foundSeeMore: HTMLElement | null = null;

      for (const el of path) {
        if (!(el instanceof HTMLElement)) continue;
        // Check see-more
        if (!foundSeeMore && el.hasAttribute('data-see-more')) {
          foundSeeMore = el;
          break;
        }
        // Check stored element references (works even if Google Maps reparents the DOM)
        if (!foundMarker) {
          const item = this.clusterPopupItems.find(i => i.el === el);
          if (item) { foundMarker = item.marker; break; }
        }
        // Fallback: check data-marker-id attribute
        if (!foundMarker && el.hasAttribute('data-marker-id')) {
          const markerId = el.getAttribute('data-marker-id');
          const pm = this._markers.find(m => m.data._id === markerId);
          if (pm) { foundMarker = pm; break; }
        }
      }

      // Strategy 2: Geometric hit testing as last resort
      if (!foundMarker && !foundSeeMore) {
        for (const item of this.clusterPopupItems) {
          const rect = item.el.getBoundingClientRect();
          if (mouseEvent.clientX >= rect.left && mouseEvent.clientX <= rect.right &&
              mouseEvent.clientY >= rect.top && mouseEvent.clientY <= rect.bottom) {
            foundMarker = item.marker;
            break;
          }
        }
      }

      // Handle see-more
      if (foundSeeMore) {
        e.stopPropagation();
        e.preventDefault();
        const clusterIdx = foundSeeMore.getAttribute('data-cluster-index');
        if (clusterIdx != null) {
          const cp = this.clusterPositions[+clusterIdx];
          this.ngZone.run(() => { cp?.el?.click(); });
        }
        return;
      }

      // Handle marker click
      if (foundMarker) {
        e.stopPropagation();
        e.preventDefault();
        this.ngZone.run(() => {
          if ('startTime' in foundMarker!.data) {
            this.goToEvent(foundMarker!.data as VisitorEvent);
          } else {
            this.goToPlace(foundMarker!.data as VisitorPlace);
          }
        });
      }
    };

    // Capture phase at document level fires BEFORE any Google Maps handler
    document.addEventListener('click', this.clusterPopupClickHandler, true);
  }

  private closeClusterInfoWindows(): void {
    this.clusterPositions.forEach((clusterPosition) => {
      if (clusterPosition.overlay) {
        clusterPosition.overlay.setMap(null);
        clusterPosition.overlay = null;
      }
      if (clusterPosition.infowindow) {
        clusterPosition.infowindow.close();
        clusterPosition.infowindow = null;
      }
      if (clusterPosition.marker) {
        clusterPosition.marker.setMap(null);
        clusterPosition.marker = null;
      }
      clusterPosition.isInfoWindowOpen = false;
    });

    this.isShowClusterInfoWindow = false;
  }

  private getPositionOfEachCluster($event: any): void {
    this.clusterPositions = [];
    this.clusterPopupItems = [];
    $event.clusters_?.forEach((cluster: any) => {
      if (cluster.clusterIcon_?.div_) {
        const rect = cluster.clusterIcon_.div_.getBoundingClientRect();
        this.clusterPositions.push({
          cluster,
          rect,
          infowindow: null,
          isInfoWindowOpen: false,
          marker: null,
          el: cluster.clusterIcon_.div_,
          overlay: null,
        });
      }
    });
  }

  private setupClusterHoverListeners(): void {
    // Bind listeners straight off clusterPositions (using each entry's own DOM
    // node) so the index passed to the hover handlers always matches the cluster
    // being hovered. Re-querying the DOM separately could yield a different
    // order/count than clusterPositions and make the popup drift to the wrong
    // cluster.
    this.clusterPositions.forEach((cp, i) => {
      const el = cp.el;
      if (!el || !this.clusterAbortController) return;

      el.addEventListener('mouseenter', () => {
        const pending = this.clusterCloseTimeouts.get(i);
        if (pending) { clearTimeout(pending); this.clusterCloseTimeouts.delete(i); }
        this.handleClusterHover(i);
      }, { signal: this.clusterAbortController.signal });

      el.addEventListener('mouseleave', () => {
        this.scheduleClusterClose(i);
      }, { signal: this.clusterAbortController.signal });
    });
  }

  private handleClusterHover(index: number): void {
    const matchingCluster = this.clusterPositions[index];
    if (!matchingCluster || matchingCluster.isInfoWindowOpen) return;

    // Close other cluster popups
    this.clusterPositions.forEach((clusterPosition, i) => {
      if (i !== index && clusterPosition.overlay) {
        clusterPosition.overlay.setMap(null);
        clusterPosition.overlay = null;
        clusterPosition.isInfoWindowOpen = false;
      }
    });

    this.showClusterInfoWindow(matchingCluster, index);
  }

  private showClusterInfoWindow(clusterPosition: IClusterPosition, index: number): void {
    try {
      this.isShowClusterInfoWindow = true;
      const container = this.generateClusterHoveringPopupContent(clusterPosition, index);
      const overlay = this.createClusterPopupOverlay(container, clusterPosition, index);
      overlay.setMap(this.googleMap.googleMap);
      clusterPosition.overlay = overlay;
      clusterPosition.isInfoWindowOpen = true;
    } catch (error) {
      console.error('❌ [GoogleMap] Error showing cluster info window:', error);
    }
  }

  /**
   * Build the cluster hover popup as a custom OverlayView so we fully control its
   * placement and tail. It renders a white card that flips above/below the
   * cluster depending on available room, with a small tail pointing at the
   * cluster (down when above, up when below) — mirroring the native InfoWindow
   * look, which can only ever point downward.
   */
  private createClusterPopupOverlay(
    content: HTMLElement,
    clusterPosition: IClusterPosition,
    clusterIndex: number
  ): google.maps.OverlayView {
    const self = this;
    const center = new google.maps.LatLng(
      clusterPosition.cluster.center_.lat(),
      clusterPosition.cluster.center_.lng()
    );

    const TAIL = 14; // px (rotated square; half of it overlaps the card edge)
    const GAP = 12; // px between the cluster circle and the card

    const overlay = new google.maps.OverlayView();
    let root: HTMLDivElement | null = null;
    let card: HTMLDivElement | null = null;
    let tail: HTMLDivElement | null = null;

    overlay.onAdd = function () {
      root = document.createElement('div');
      root.style.position = 'absolute';
      root.style.pointerEvents = 'none'; // transparent wrapper; card re-enables
      root.style.zIndex = '2000';

      card = document.createElement('div');
      card.style.position = 'relative';
      card.style.boxSizing = 'border-box';
      card.style.pointerEvents = 'auto';
      card.style.background = '#ffffff';
      card.style.borderRadius = '16px';
      card.style.boxShadow = '0 12px 28px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.10)';
      card.style.width = `${Math.min(360, Math.max(260, Math.round(window.innerWidth * 0.2)))}px`;
      card.appendChild(content);

      // Tail: a white rotated square. The inner half blends into the white card;
      // the outer half (with its shadow) reads as the speech-bubble pointer.
      tail = document.createElement('div');
      tail.style.position = 'absolute';
      tail.style.left = '50%';
      tail.style.width = `${TAIL}px`;
      tail.style.height = `${TAIL}px`;
      tail.style.marginLeft = `${-TAIL / 2}px`;
      tail.style.background = '#ffffff';
      tail.style.transform = 'rotate(45deg)';
      card.appendChild(tail);

      // Close button (red circle), matching the previous styling.
      const close = document.createElement('div');
      close.style.cssText = `
        position:absolute; top:-12px; right:-12px;
        width:26px; height:26px; border-radius:50%;
        background:#ef4444; color:#fff;
        display:flex; align-items:center; justify-content:center;
        font-size:13px; font-weight:bold; line-height:1;
        cursor:pointer; z-index:5;
        box-shadow:0 2px 6px rgba(0,0,0,0.25);
      `;
      close.textContent = '✕';
      close.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        self.ngZone.run(() => self.closeClusterPopup(clusterIndex));
      });
      card.appendChild(close);

      // Keep the popup open while the pointer is over it.
      if (self.clusterAbortController) {
        root.addEventListener('mouseenter', () => {
          const pending = self.clusterCloseTimeouts.get(clusterIndex);
          if (pending) { clearTimeout(pending); self.clusterCloseTimeouts.delete(clusterIndex); }
        }, { signal: self.clusterAbortController.signal });
        root.addEventListener('mouseleave', () => {
          self.scheduleClusterClose(clusterIndex);
        }, { signal: self.clusterAbortController.signal });
      }

      root.appendChild(card);
      this.getPanes()?.floatPane.appendChild(root);
    };

    overlay.draw = function () {
      if (!root || !card || !tail) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(center);
      if (!point) return;

      const popupW = card.offsetWidth;
      const popupH = card.offsetHeight;
      if (!popupW || !popupH) return;

      const clusterRect =
        clusterPosition.el?.getBoundingClientRect() ?? clusterPosition.rect;
      const clusterRadius = clusterRect ? clusterRect.height / 2 : 22;

      // Flip below when there isn't enough room above the cluster.
      const mapRect = self.googleMap?.googleMap?.getDiv()?.getBoundingClientRect();
      const spaceAbove =
        clusterRect && mapRect ? clusterRect.top - mapRect.top : Number.POSITIVE_INFINITY;
      const below = spaceAbove < popupH + GAP + TAIL + 8;

      root.style.left = `${Math.round(point.x - popupW / 2)}px`;
      if (below) {
        root.style.top = `${Math.round(point.y + clusterRadius + GAP)}px`;
        tail.style.top = `${-TAIL / 2}px`;
        tail.style.bottom = '';
        tail.style.boxShadow = '-3px -3px 6px rgba(0,0,0,0.06)';
      } else {
        root.style.top = `${Math.round(point.y - clusterRadius - GAP - popupH)}px`;
        tail.style.bottom = `${-TAIL / 2}px`;
        tail.style.top = '';
        tail.style.boxShadow = '3px 3px 6px rgba(0,0,0,0.06)';
      }
    };

    overlay.onRemove = function () {
      if (root && root.parentNode) {
        root.parentNode.removeChild(root);
      }
      root = card = tail = null;
    };

    return overlay;
  }

  private scheduleClusterClose(index: number): void {
    const timeout = setTimeout(() => {
      this.clusterCloseTimeouts.delete(index);
      this.closeClusterPopup(index);
    }, 150);
    this.clusterCloseTimeouts.set(index, timeout);
  }

  private closeClusterPopup(index: number): void {
    const cp = this.clusterPositions[index];
    if (cp?.overlay) {
      cp.overlay.setMap(null);
      cp.overlay = null;
    }
    if (cp) {
      cp.isInfoWindowOpen = false;
    }
    this.isShowClusterInfoWindow = false;
  }

  private generateClusterHoveringPopupContent(clusterPosition: IClusterPosition, clusterIndex: number): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'p-4';

    const grid = document.createElement('div');
    const gridCols = clusterPosition.cluster.markers_.length === 2 ? 2 : 3;
    grid.className = `grid grid-cols-${gridCols} bg-white gap-3`;

    clusterPosition.cluster.markers_.slice(0, 6).forEach((marker: any) => {
      // The raw clusterer marker's title is set from our `[title]="marker.title"`
      // binding, so it always equals the source marker's own title — match on that
      // instead of assuming title carries the _id (event map now uses name for the
      // hover tooltip, while place/trip markers still put _id in title).
      const placeMarker = this._markers.find((m) => m.title === marker.title);

      if (placeMarker) {
        const div = document.createElement('div');
        div.innerHTML = `
          <div class="flex flex-col gap-2 items-center overflow-hidden cursor-pointer group">
            <img src="${
              placeMarker.data.cover?.source || ''
            }" class="w-full aspect-square object-cover rounded-md group-hover:scale-[1.05] transition-all" />
            <p class="text-sm group-hover:font-semibold group-hover:text-gray-600 transition-all">${
              placeMarker.data.name || placeMarker.data.title || 'Untitled'
            }</p>
          </div>
        `;

        div.setAttribute('data-marker-id', placeMarker.data._id);
        div.style.pointerEvents = 'auto';
        div.style.cursor = 'pointer';
        div.style.position = 'relative';
        div.style.zIndex = '10';

        this.clusterPopupItems.push({ el: div, marker: placeMarker });
        this._renderer.appendChild(grid, div);
      }
    });

    this._renderer.appendChild(container, grid);

    if (clusterPosition.cluster.markers_.length > 6) {
      const moreDiv = document.createElement('div');
      moreDiv.innerHTML = `
        <div class="flex flex-col gap-2 items-center overflow-hidden mt-4">
          <p class="text-sm bg-blue-400 text-white px-3 py-1.5 rounded-sm uppercase cursor-pointer font-semibold">Click to see more</p>
        </div>
      `;

      moreDiv.setAttribute('data-see-more', 'true');
      moreDiv.setAttribute('data-cluster-index', String(clusterIndex));
      moreDiv.style.pointerEvents = 'auto';
      moreDiv.style.cursor = 'pointer';
      moreDiv.style.position = 'relative';
      moreDiv.style.zIndex = '10';

      this._renderer.appendChild(container, moreDiv);
    }

    return container;
  }

  private removeClusterListener(): void {
    this.clusterAbortController?.abort();
  }

  setNoContent(): void {
    this.center = ConfigService.config.google.USCenter as google.maps.LatLngLiteral;
    this.options = {
      ...this.options,
      zoom: 5,
    };
  }

  // Getters for shadow DOM access
  public get imgoingWrapper(): HTMLElement | null {
    return (
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix')
    );
  }

  public get shadowRoot(): ShadowRoot | null {
    const wrapper = this.imgoingWrapper || document;
    const appRoot = wrapper.querySelector('app-root') as any;
    return appRoot?.shadowRoot || null;
  }

  // Cleanup
  ngOnDestroy(): void {
    this.debug(' ngOnDestroy called');
    if (this.clusterPopupClickHandler) {
      document.removeEventListener('click', this.clusterPopupClickHandler, true);
      this.clusterPopupClickHandler = null;
    }
    this.clusterAbortController?.abort();
    this.clusterCloseTimeouts.forEach(t => clearTimeout(t));
    this.clusterCloseTimeouts.clear();
    this.closeClusterInfoWindows();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Public methods for external use
  public retryInitialization(): void {
    this.debug('Retrying initialization');
    this.resetComponentState();
    this.startInitializationSequence();
  }

  public getHealthStatus(): {
    mapInitialized: boolean;
    apiReady: boolean;
    initializationComplete: boolean;
    markersCount: number;
    pendingMarkersCount: number;
    isFullyReady: boolean;
  } {
    return {
      mapInitialized: this.mapInitialized,
      apiReady: this.apiReady,
      initializationComplete: this.initializationComplete,
      markersCount: this._markers.length,
      pendingMarkersCount: this.pendingMarkers.length,
      isFullyReady: this.isFullyReady(),
    };
  }

  public forceProcessMarkers(): void {
    this.debug('Force processing markers');
    if (this.isFullyReady()) {
      this.processMarkersWithDelay();
    } else {
      console.warn('⚠️ [GoogleMap] Cannot force process markers - component not ready');
    }
  }

  // TrackBy function for markers to improve performance
  trackByMarkerId(
    index: number,
    marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): any {
    return marker.data?._id || marker.data?.id || index;
  }

  // Method to check if all required services are loaded
  private areServicesReady(): boolean {
    return !!(
      this.googleMapService &&
      this.modalService &&
      this.mapDirectionsService &&
      this._placeService
    );
  }

  // Method to validate component dependencies
  private validateDependencies(): boolean {
    const missingServices = [];

    if (!this.googleMapService) missingServices.push('GoogleMapService');
    if (!this.modalService) missingServices.push('ModalService');
    if (!this.mapDirectionsService) missingServices.push('MapDirectionsService');
    if (!this._placeService) missingServices.push('VisitorPlaceService');

    if (missingServices.length > 0) {
      console.error('[GoogleMap] Missing required services:', missingServices);
      return false;
    }

    return true;
  }

  // Enhanced error handling for template operations
  handleTemplateError(error: any, operation: string): void {
    console.error(`[GoogleMap] Template error in ${operation}:`, error);

    // You can add more sophisticated error handling here
    // For example, showing a notification to the user
    // this.notificationService.showError(`Map ${operation} failed`);
  }

  // Safe method to access nested properties
  safeGetProperty<T>(obj: any, path: string, defaultValue: T): T {
    try {
      return path.split('.').reduce((o, p) => o && o[p], obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private isScriptExist(src: string): boolean {
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].getAttribute('src') === src) {
        return true;
      }
    }
    return false;
  }

  private handleEventDetailsClick(event: VisitorEvent): void {
    if (this.ssrDetailNavigationService.navigateToDetail({
      itemType: 'event',
      item: event,
      hubName: this.hubName,
      frontendSettings: this.frontendSettings,
      onPreviewClosed: () => this.onDetailsWindowClose.emit(event),
    })) {
      if (!this.ssrDetailNavigationService.isSsrTripPlannerWidget()) this.onInfoWindowClick.emit(event);
      return;
    }

    try {
      const modal = this.modalService.show({
        component: FancyEventDetailsModalComponent,
        panelClass: 'ig-modal-w-full',
        data: {
          currentEvent: event,
          allEvents: [],
          favCustomEvents: [],
          from: 'placeCalendar',
        } as IEventDetailsModalData,
      });

      this.onInfoWindowClick.emit(event);

      modal.beforeClosed().subscribe(() => {
        this.onDetailsWindowClose.emit(event);
      });
    } catch (error) {
      console.error('❌ [GoogleMap] Error opening event modal:', error);
    }
  }

  private handlePlaceDetailsClick(place: VisitorPlace): void {
    if (this.ssrDetailNavigationService.navigateToDetail({
      itemType: 'place',
      item: place,
      hubName: this.hubName,
      frontendSettings: this.frontendSettings,
      onPreviewClosed: () => this.onDetailsWindowClose.emit(place),
    })) {
      if (!this.ssrDetailNavigationService.isSsrTripPlannerWidget()) this.onInfoWindowClick.emit(place);
      return;
    }

    try {
      const modal = this.modalService.show({
        component: FancyPlaceDetailsModalComponent,
        panelClass: 'ig-modal-w-full',
        data: {
          currentPlace: place,
        } as IPlaceDetailsModalData,
      });

      modal.beforeClosed().subscribe(() => {
        this.onDetailsWindowClose.emit(place);
      });

      this.onInfoWindowClick.emit(place);
    } catch (error) {
      console.error('❌ [GoogleMap] Error opening place modal:', error);
    }
  }

  private createInfoWindowContent(
    markerData: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>
  ): string {
    const data = markerData.data;

    // Check if it's an event or place based on available properties
    if ('eventLink' in data) {
      // It's an event
      const event = data as VisitorEvent;
      return `
        <div id="info-window-content" style="width: 200px; height:200px; aspect-ratio: 1; cursor: pointer;">
          <img src="${
            event.cover?.source
          }" alt="Place Image" style="width: 100%; height: 100%; object-fit: cover;"></img>
          <div style="position: absolute; bottom: 12px; left: 12px;">
            <strong style="margin: 0 0 10px 0; font-size: 16px;color: white;">${
              event.name || event.title
            }</strong>
          <p style="margin: 2px 0; font-size: 14px;color: white;">${event.timeOneLine}</p>
          </div>
        </div>
      `;
    } else {
      // It's a place
      const place = data as VisitorPlace;
      return `
        <div id="info-window-content" style="width: 200px; height:200px; aspect-ratio: 1; cursor: pointer;">
          <img src="${
            place.cover?.source
          }" alt="Place Image" style="width: 100%; height: 100%; object-fit: cover;"></img>
          <div style="position: absolute; padding: 12px; bottom: 0; left: 0; width:100%; background: linear-gradient(to bottom, transparent, black);">
            <strong style="margin: 0 0 10px 0; font-size: 16px;color: white;">${
              place.name || place.title
            }</strong>
          ${
            place.category
              ? `<p style="margin: 2px 0; font-size: 14px;color: white;">${place.category}</p>`
              : ''
          }
          </div>
        </div>
      `;
    }
  }
}
